import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { DirectPaymentMethodSchema } from '../direct-payment-method/direct-payment-method';
import {
  ORDER_NOTE_MAX_LENGTH,
  ORDER_REASON_MAX_LENGTH,
} from '../direct-payment-order/direct-payment-order';
import { ProductKeySchema } from '../product-key/product-key';

/**
 * Recording a payment that happened off-platform.
 *
 * ## What a staff member does NOT send: the money
 *
 * There is no `listAmount`, no `discountAmount`, no `chargedAmount` and no
 * `grantIds`. Every one of them is resolved **server-side** — the price from
 * `billing.directPrices` in the member's own gender scope (U15), the discount
 * from the member's earned grants over the QueryBus (N6) — because a price a
 * client could name is a price a client could choose. That is the same reasoning
 * N14 applies to entitlements: the server is the authority on what something
 * costs, not the surface that displays it.
 *
 * The write contract is therefore what keeps the client out, while the
 * repository's `fillable` still has to list every one of those columns because a
 * *service* sets them (P22a — `fillable` gates the create too).
 *
 * ## The order opens as a `draft`
 *
 * Creating one claims nothing and commits to nothing: it quotes the terms so a
 * staff member can see them. {@link DirectPaymentOrderSubmitSchema} is the write
 * that fixes the price and reserves the member's grants.
 *
 * ## What a staff member does NOT send either: the receipt
 *
 * `evidenceMediaId` is absent, and it was **removed** here (P23b4) rather than
 * never added: until then this schema accepted any uuid and bound it into
 * `evidence_media_id` with nothing checking the asset's kind, so a `billing.manage`
 * operator could point an order at a member's `id_document` — and, once detaching
 * schedules a retention purge, destroy verification evidence they may not even
 * read. A uuid a client can name is a uuid a client can aim, exactly as a price a
 * client can name is a price a client can choose.
 *
 * The receipt is attached through the order's own evidence routes instead, which
 * mint and finalize the asset themselves and therefore *know* what they bound.
 * It stays on the read DTO ({@link DirectPaymentOrderSchema}), because reading
 * which asset an order points at was never the problem.
 */
export const DirectPaymentOrderCreateSchema = z
  .object({
    userId: UuidSchema,
    productKey: ProductKeySchema,
    method: DirectPaymentMethodSchema,
    note: z.string().trim().max(ORDER_NOTE_MAX_LENGTH).nullable().default(null),
  })
  .strict();

export type DirectPaymentOrderCreateRequest = z.infer<typeof DirectPaymentOrderCreateSchema>;

/**
 * Editing a draft.
 *
 * **The member and the product are not editable**, which is why this is not
 * `DirectPaymentOrderCreateSchema.partial()`. Changing either would change what
 * the order is *for*, and re-pricing an existing row in place would leave its
 * audit trail describing a purchase that no longer exists — a different order is
 * a different order. What remains editable is everything that describes the
 * payment rather than the purchase.
 */
export const DirectPaymentOrderUpdateSchema = DirectPaymentOrderCreateSchema.omit({
  userId: true,
  productKey: true,
})
  .partial()
  .strict();

export type DirectPaymentOrderUpdateRequest = z.infer<typeof DirectPaymentOrderUpdateSchema>;

/**
 * Putting a draft up for confirmation — the write that **claims** the member's
 * discount grants (N27) and freezes the price.
 *
 * It carries no fields. The terms are re-resolved at this moment rather than
 * read off the draft: a draft entered last week quotes last week's price, and
 * the number a member is actually asked to pay must be the one in force when the
 * order commits. Sending an empty `.strict()` object rather than nothing keeps
 * the shape available for a future `expectedChargedAmount` confirmation guard
 * without changing the route.
 */
export const DirectPaymentOrderSubmitSchema = z.object({}).strict();

export type DirectPaymentOrderSubmitRequest = z.infer<typeof DirectPaymentOrderSubmitSchema>;

/**
 * Refusing an order, and giving the member's claimed grants back.
 *
 * The reason is **required**. A rejection is the branch where a member paid, or
 * believes they paid, and got nothing; "why" is the first thing support will be
 * asked and the last thing anyone will be able to reconstruct afterwards.
 */
export const DirectPaymentOrderRejectSchema = z
  .object({
    reason: z.string().trim().min(1).max(ORDER_REASON_MAX_LENGTH),
  })
  .strict();

export type DirectPaymentOrderRejectRequest = z.infer<typeof DirectPaymentOrderRejectSchema>;

/**
 * Recording that a confirmed order was refunded, which **revokes** the
 * entitlement it granted.
 *
 * Required reason, for the reason above and one more: `03-payments-iap.md` is
 * explicit that a ban never entitles a member to a refund, so a refund is always
 * a decision somebody made against the default — and the record has to say who
 * and why. It does not return the discount grants
 * (`REFUND_KEEPS_GRANTS_SPENT`).
 */
export const DirectPaymentOrderRefundSchema = z
  .object({
    reason: z.string().trim().min(1).max(ORDER_REASON_MAX_LENGTH),
  })
  .strict();

export type DirectPaymentOrderRefundRequest = z.infer<typeof DirectPaymentOrderRefundSchema>;
